import { TextFieldWithLimit } from '@/components/ui/TextFieldWithLimit';
import type { Webinar } from '@/features/webinar/types';
import { ChevronDown, Info, Plus, Trash2 } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import {
  createSpeaker,
  deleteSpeaker,
  deleteSpeakerPicture,
  listPresenters,
  listSpeakers,
  listWorkspaceAccesses,
  updateSpeaker,
  updateWebinar,
  uploadSpeakerPicture,
} from '@/features/webinar/webinarThunks';
import { useFieldArray, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import type { RootState } from '@/app/store';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Button } from '@/components/ui/button';
import Loader from '@/components/ui/Loader';
import type { AppError } from '@/features/auth/types';
import { mapHttpError } from '@/lib/mapApiError';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Switch } from '@/components/ui/switch';
import { selectUsage } from '@/features/billing/billingSlice';
import LiveRolesUsageBanner from '@/components/billing/LiveRolesUsageBanner';
import {
  countSpeakerFormRows,
  getLiveRolesMax,
  isLiveRolesAtMax,
  isSpeakerRowEmpty,
  wouldExceedLiveRolesLimit,
} from '@/lib/liveRolesLimit';

const speakerRowSchema = z.object({
  speakerId: z.number().optional(),
  fullName: z.string(),
  jobTitle: z.string(),
  email: z.string(),
  pictureUrl: z.string().nullish(),
});

const ACCEPTED_PICTURE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
const MAX_PICTURE_SIZE = 5 * 1024 * 1024;

const createSpeakersFormSchema = (t: (key: string) => string) =>
  z
    .object({
      speakers: z.array(speakerRowSchema),
    })
    .superRefine((data, ctx) => {
      data.speakers.forEach((row, index) => {
        if (isSpeakerRowEmpty(row)) return;

        if (!row.fullName.trim()) {
          ctx.addIssue({
            code: 'custom',
            message: t('speakersForm.validation.fullNameRequired'),
            path: ['speakers', index, 'fullName'],
          });
        }
        if (!row.jobTitle.trim()) {
          ctx.addIssue({
            code: 'custom',
            message: t('speakersForm.validation.jobTitleRequired'),
            path: ['speakers', index, 'jobTitle'],
          });
        }
        if (!row.email.trim()) {
          ctx.addIssue({
            code: 'custom',
            message: t('speakersForm.validation.emailRequired'),
            path: ['speakers', index, 'email'],
          });
        } else if (!z.email().safeParse(row.email.trim()).success) {
          ctx.addIssue({
            code: 'custom',
            message: t('speakersForm.validation.emailInvalid'),
            path: ['speakers', index, 'email'],
          });
        }
      });
    });

type SpeakerFormItem = z.infer<typeof speakerRowSchema>;
type SpeakersFormValues = z.infer<ReturnType<typeof createSpeakersFormSchema>>;

interface SpeakersFormProps {
  webinar?: Webinar | null;
  onSuccess?: () => void;
}

export default function SpeakersForm({ webinar, onSuccess }: SpeakersFormProps) {
  const { t } = useTranslation('createWebinar');
  const { t: tCommon } = useTranslation('common');
  const dispatch = useAppDispatch();
  const speakers = useAppSelector((state: RootState) => state.webinar.speakers);
  const allPresenters = useAppSelector((state: RootState) => state.webinar.presenters);
  const speakersLoading = useAppSelector((state: RootState) => state.webinar.speakersLoading);
  const usage = useAppSelector(selectUsage);
  const maxLiveRoles = getLiveRolesMax(usage);
  const workspaceId = useAppSelector((state: RootState) => state.workspace.currentWorkspace?.id);
  const { currentWebinarSession } = useAppSelector((state: RootState) => state.webinar);

  useEffect(() => {
    if (webinar?.workspaceId) {
      void dispatch(
        listWorkspaceAccesses({
          workspaceId: webinar.workspaceId,
          page: 0,
          size: 100,
        }),
      );
    }
  }, [dispatch, webinar?.workspaceId]);

  useEffect(() => {
    if (webinar?.id && webinar.workspaceId) {
      void dispatch(
        listSpeakers({
          workspaceId: webinar.workspaceId,
          webinarId: webinar.id,
          page: 0,
          size: 10,
        }),
      );
      void dispatch(listPresenters({ workspaceId: webinar.workspaceId, webinarId: webinar.id }));
    }
  }, [dispatch, webinar?.id, webinar?.workspaceId]);

  const webinarSpeakers = speakers.filter((s) => s.webinarId === webinar?.id);
  const webinarPresenters = allPresenters.filter((p) => webinar?.id && p.webinarId === webinar.id);

  const initialSpeakerIdsRef = useRef<Set<number>>(new Set());
  const lastSyncedWebinarIdRef = useRef<number | undefined>(undefined);
  const lastSyncedSpeakersSignatureRef = useRef<string | null>(null);

  const {
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
    setValue,
    watch,
    getValues,
    control,
    reset,
  } = useForm<SpeakersFormValues>({
    resolver: zodResolver(createSpeakersFormSchema(t)),
    defaultValues: {
      speakers:
        webinarSpeakers.length > 0
          ? webinarSpeakers.map<SpeakerFormItem>((s) => ({
              speakerId: s.id,
              fullName: s.displayName,
              jobTitle: s.jobTitle,
              email: s.temporaryGuestEmail ?? '',
              pictureUrl: s.guestPictureUrl ?? '',
            }))
          : [],
    },
    mode: 'onChange',
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'speakers',
  });

  useEffect(() => {
    if (!webinar?.id || webinar.workspaceId == null) return;
    if (speakersLoading) return;

    const ws = speakers.filter((s) => s.webinarId === webinar.id);
    const signature = ws
      .map((s) => `${s.id}:${s.displayName}:${s.jobTitle}:${s.temporaryGuestEmail ?? ''}`)
      .sort((a, b) => a.localeCompare(b))
      .join('||');

    const webinarChanged = lastSyncedWebinarIdRef.current !== webinar.id;
    if (!webinarChanged && lastSyncedSpeakersSignatureRef.current === signature) {
      return;
    }

    lastSyncedWebinarIdRef.current = webinar.id;
    lastSyncedSpeakersSignatureRef.current = signature;

    const nextSpeakers: SpeakerFormItem[] =
      ws.length > 0
        ? ws.map((s) => ({
            speakerId: s.id,
            fullName: s.displayName,
            jobTitle: s.jobTitle,
            email: s.temporaryGuestEmail ?? '',
            pictureUrl: s.guestPictureUrl ?? '',
          }))
        : [];

    reset({ speakers: nextSpeakers });
    initialSpeakerIdsRef.current = new Set(ws.map((s) => s.id));
  }, [webinar?.id, webinar?.workspaceId, reset, speakers, speakersLoading]);

  const speakersValues = watch('speakers');
  const presenterCount = webinarPresenters.length;
  const guestCount = countSpeakerFormRows(speakersValues ?? []);
  const isAtMax = isLiveRolesAtMax(presenterCount + guestCount, maxLiveRoles);

  const onSubmit = async (data: SpeakersFormValues) => {
    if (!webinar?.id || !webinar.workspaceId) {
      console.error('Webinar ou workspace manquant pour créer un intervenant');
      return;
    }

    const activeSpeakers = data.speakers.filter((row) => !isSpeakerRowEmpty(row));

    if (wouldExceedLiveRolesLimit(presenterCount, activeSpeakers.length, maxLiveRoles)) {
      toast.error(t('liveRolesLimit.exceeded'));
      return;
    }

    let allSuccess = true;
    let hasErrors = false;

    const currentSpeakerIds = new Set(
      activeSpeakers.map((item) => item.speakerId).filter((id): id is number => id !== undefined),
    );

    const speakersToDelete = Array.from(initialSpeakerIdsRef.current).filter(
      (id) => !currentSpeakerIds.has(id),
    );

    for (const speakerIdToDelete of speakersToDelete) {
      const result = await dispatch(
        deleteSpeaker({
          workspaceId: webinar.workspaceId,
          webinarId: webinar.id,
          speakerId: speakerIdToDelete,
          skipListSpeakers: true,
        }),
      );

      if (deleteSpeaker.rejected.match(result)) {
        allSuccess = false;
        hasErrors = true;
        const error = result.payload as AppError | undefined;
        if (error?.message && !(Array.isArray(error.details) && error.details.length > 0)) {
          const mainMessage = tCommon(mapHttpError(error.message), error.message);
          toast.error(mainMessage);
        }
        if (error?.details && Array.isArray(error.details)) {
          error.details.forEach((detail: { message?: string; fieldName?: string }) => {
            if (detail.message) {
              const mappedMessage = mapHttpError(detail.message);
              const translatedMessage = tCommon(mappedMessage, detail.message);
              toast.error(translatedMessage);
            }
          });
        }
      }
    }

    for (let index = 0; index < data.speakers.length; index++) {
      const item = data.speakers[index];
      if (isSpeakerRowEmpty(item)) continue;

      const fieldKey = fields[index]?.id ?? String(index);

      if (item.speakerId) {
        const result = await dispatch(
          updateSpeaker({
            workspaceId: webinar.workspaceId,
            webinarId: webinar.id,
            speakerId: item.speakerId,
            input: {
              overrideFullName: item.fullName.trim(),
              jobTitle: item.jobTitle.trim(),
              userId: null,
              guestFullName: item.fullName.trim(),
              guestEmail: item.email.trim(),
            },
            skipListSpeakers: true,
          }),
        );

        if (updateSpeaker.rejected.match(result)) {
          allSuccess = false;
          hasErrors = true;
          const error = result.payload as AppError | undefined;
          if (error?.message && !(Array.isArray(error.details) && error.details.length > 0)) {
            const mainMessage = tCommon(mapHttpError(error.message), error.message);
            toast.error(mainMessage);
          }
          if (error?.details && Array.isArray(error.details)) {
            error.details.forEach((detail: { message?: string; fieldName?: string }) => {
              if (detail.message) {
                const mappedMessage = mapHttpError(detail.message);
                const translatedMessage = tCommon(mappedMessage, detail.message);
                toast.error(translatedMessage);
              }
            });
          }
        } else {
          // Synchronisation de la photo sur un invité existant
          if (pictureFiles[fieldKey]) {
            await dispatch(
              uploadSpeakerPicture({
                workspaceId: webinar.workspaceId,
                webinarId: webinar.id,
                speakerId: item.speakerId,
                file: pictureFiles[fieldKey],
                skipListSpeakers: true,
              }),
            );
          } else if (pictureRemovals[fieldKey] && item.pictureUrl) {
            await dispatch(
              deleteSpeakerPicture({
                workspaceId: webinar.workspaceId,
                webinarId: webinar.id,
                speakerId: item.speakerId,
                skipListSpeakers: true,
              }),
            );
          }
        }
      } else {
        const result = await dispatch(
          createSpeaker({
            workspaceId: webinar.workspaceId,
            webinarId: webinar.id,
            input: {
              overrideFullName: item.fullName.trim(),
              jobTitle: item.jobTitle.trim(),
              userId: null,
              guestFullName: item.fullName.trim(),
              guestEmail: item.email.trim(),
            },
            file: pictureFiles[fieldKey] ?? null,
            skipListSpeakers: true,
          }),
        );

        if (createSpeaker.fulfilled.match(result)) {
          const createdId = result.payload;
          if (typeof createdId === 'number') {
            setValue(`speakers.${index}.speakerId`, createdId, { shouldDirty: false });
          }
        } else if (createSpeaker.rejected.match(result)) {
          allSuccess = false;
          hasErrors = true;
          const error = result.payload as AppError | undefined;
          if (error?.message && !(Array.isArray(error.details) && error.details.length > 0)) {
            const mainMessage = tCommon(mapHttpError(error.message), error.message);
            toast.error(mainMessage);
          }
          if (error?.details && Array.isArray(error.details)) {
            error.details.forEach((detail: { message?: string; fieldName?: string }) => {
              if (detail.message) {
                const mappedMessage = mapHttpError(detail.message);
                const translatedMessage = tCommon(mappedMessage, detail.message);
                toast.error(translatedMessage);
              }
            });
          }
        }
      }
    }

    await dispatch(
      listSpeakers({
        workspaceId: webinar.workspaceId,
        webinarId: webinar.id,
        page: 0,
        size: 20,
      }),
    );

    if (allSuccess && !hasErrors) {
      toast.success(t('speakersForm.speakersSavedSuccess'));
      onSuccess?.();
    }
  };

  const addSpeakerForm = () => {
    if (isAtMax) {
      toast.error(t('liveRolesLimit.exceeded'));
      return;
    }
    append({
      speakerId: undefined,
      fullName: '',
      jobTitle: '',
      email: '',
    });
  };
  const [deletingSpeakerId, setDeletingSpeakerId] = useState<number | null>(null);

  // Photo par ligne, indexée par l'id stable du champ (field.id)
  const [pictureFiles, setPictureFiles] = useState<Record<string, File>>({});
  const [pictureRemovals, setPictureRemovals] = useState<Record<string, boolean>>({});
  const [pictureErrors, setPictureErrors] = useState<Record<string, string>>({});
  const [picturePreviews, setPicturePreviews] = useState<Record<string, string>>({});

  const [expandedSpeakerRows, setExpandedSpeakerRows] = useState<Set<string>>(new Set());
  const toggleSpeakerRow = (fieldId: string) => {
    setExpandedSpeakerRows((prev) => {
      const next = new Set(prev);
      if (next.has(fieldId)) {
        next.delete(fieldId);
      } else {
        next.add(fieldId);
      }
      return next;
    });
  };

  useEffect(() => {
    const urls: Record<string, string> = {};
    Object.entries(pictureFiles).forEach(([key, file]) => {
      urls[key] = URL.createObjectURL(file);
    });
    setPicturePreviews(urls);
    return () => {
      Object.values(urls).forEach((url) => URL.revokeObjectURL(url));
    };
  }, [pictureFiles]);

  const handlePictureSelect = useCallback(
    (fieldKey: string, file: File | undefined) => {
      if (!file) {
        return;
      }
      if (!ACCEPTED_PICTURE_TYPES.includes(file.type)) {
        setPictureErrors((prev) => ({
          ...prev,
          [fieldKey]: t('speakersForm.validation.pictureInvalidType'),
        }));
        return;
      }
      if (file.size > MAX_PICTURE_SIZE) {
        setPictureErrors((prev) => ({
          ...prev,
          [fieldKey]: t('speakersForm.validation.pictureTooLarge'),
        }));
        return;
      }
      setPictureErrors((prev) => {
        const next = { ...prev };
        delete next[fieldKey];
        return next;
      });
      setPictureRemovals((prev) => {
        const next = { ...prev };
        delete next[fieldKey];
        return next;
      });
      setPictureFiles((prev) => ({ ...prev, [fieldKey]: file }));
    },
    [t],
  );

  const handlePictureRemove = useCallback((fieldKey: string) => {
    setPictureFiles((prev) => {
      const next = { ...prev };
      delete next[fieldKey];
      return next;
    });
    setPictureErrors((prev) => {
      const next = { ...prev };
      delete next[fieldKey];
      return next;
    });
    setPictureRemovals((prev) => ({ ...prev, [fieldKey]: true }));
  }, []);

  const handleDeletePersistedSpeaker = useCallback(
    async (speakerId: number) => {
      if (!webinar?.id || !webinar.workspaceId) return;
      setDeletingSpeakerId(speakerId);
      try {
        const result = await dispatch(
          deleteSpeaker({
            workspaceId: webinar.workspaceId,
            webinarId: webinar.id,
            speakerId,
          }),
        );

        if (deleteSpeaker.fulfilled.match(result)) {
          initialSpeakerIdsRef.current.delete(speakerId);
          const rows = getValues('speakers');
          const formIndex = rows.findIndex((r) => r.speakerId === speakerId);
          if (formIndex >= 0) {
            remove(formIndex);
          }
          toast.success(t('speakersForm.speakerDeletedSuccess'));
        } else if (deleteSpeaker.rejected.match(result)) {
          const error = result.payload as AppError | undefined;
          if (error?.message && !(Array.isArray(error.details) && error.details.length > 0)) {
            toast.error(tCommon(mapHttpError(error.message), error.message));
          }
          if (error?.details && Array.isArray(error.details)) {
            error.details.forEach((detail: { message?: string }) => {
              if (detail.message) {
                toast.error(tCommon(mapHttpError(detail.message), detail.message));
              }
            });
          }
        }
      } finally {
        setDeletingSpeakerId(null);
      }
    },
    [dispatch, getValues, remove, t, tCommon, webinar?.id, webinar?.workspaceId],
  );

  const handleRemoveSpeakerRow = useCallback(
    (index: number) => {
      const row = getValues(`speakers.${index}`);
      if (row.speakerId != null) {
        void handleDeletePersistedSpeaker(row.speakerId);
        return;
      }
      remove(index);
    },
    [getValues, handleDeletePersistedSpeaker, remove],
  );

  const handleCreateAccessCodesChange = async (checked: boolean) => {
    if (!workspaceId || !webinar?.id) {
      toast.error(tCommon('errors.generic', 'Webinar ou workspace introuvable.'));
      return;
    }
    const result = await dispatch(
      updateWebinar({
        workspaceId,
        id: webinar.id,
        input: {
          title: webinar.title,
          shortDescription: webinar.shortDescription,
          description: webinar.description,
          accessCodeEnabled: checked,
        },
      }),
    );
    if (updateWebinar.rejected.match(result)) {
      const error = result.payload as AppError | undefined;
      toast.error(error?.message ? tCommon(error.message) : tCommon('errors.generic'));
    } else {
      toast.success(
        checked ? t('speakersForm.accessCodesEnabled') : t('speakersForm.accessCodesDisabled'),
      );
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="flex flex-col gap-4">
        <LiveRolesUsageBanner
          max={maxLiveRoles}
          presentersCount={presenterCount}
          guestsCount={guestCount}
        />
        <div className="flex flex-col gap-4 pb-4">
          <div className="flex items-center gap-2 justify-between">
            <div className="flex items-center gap-2">
              <p className="text-base">{t('speakersForm.createAccessCodes')}</p>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="size-5 inline-block cursor-help" />
                </TooltipTrigger>
                <TooltipContent className="max-w-xs text-sm">
                  <p>{t('speakersForm.accessCodeTooltip')}</p>
                </TooltipContent>
              </Tooltip>
            </div>
            <Switch
              defaultChecked={currentWebinarSession?.accessCodeEnabled}
              onCheckedChange={handleCreateAccessCodesChange}
            />
          </div>

          <div className="flex flex-col gap-2">
            {fields.map((field, index) => {
              const value = speakersValues?.[index];
              const fieldErrors = (errors.speakers?.[index] ?? {}) as
                | {
                    fullName?: { message?: string };
                    jobTitle?: { message?: string };
                    email?: { message?: string };
                  }
                | undefined;
              const canRemoveRow = !speakersLoading && deletingSpeakerId == null;
              const trimmedName = value?.fullName?.trim() ?? '';
              const previewSrc =
                picturePreviews[field.id] ||
                (!pictureRemovals[field.id] ? value?.pictureUrl || '' : '');
              const initials = trimmedName
                .split(' ')
                .map((n) => n.charAt(0))
                .join('')
                .toUpperCase()
                .slice(0, 2);
              const isSaved = value?.speakerId != null;
              const isExpanded = !isSaved || expandedSpeakerRows.has(field.id);

              return (
                <div key={field.id} className="flex items-start gap-2">
                  <div className="flex-1 overflow-hidden rounded-lg border border-input bg-white px-4 py-4">
                    <div className="flex flex-col gap-4">
                      {isSaved ? (
                        <button
                          type="button"
                          onClick={() => toggleSpeakerRow(field.id)}
                          className={`flex w-full items-center gap-2 text-left ${
                            isExpanded ? 'pb-2 border-b border-input' : ''
                          }`}
                        >
                          <Avatar className="size-8">
                            <AvatarImage src={previewSrc || undefined} className="object-cover" />
                            <AvatarFallback>{initials || '?'}</AvatarFallback>
                          </Avatar>
                          <span className="text-sm font-medium">
                            {trimmedName.length > 0 ? trimmedName : t('speakersForm.guest')}
                          </span>
                          <ChevronDown
                            className={`ml-auto size-4 shrink-0 transition-transform ${
                              isExpanded ? 'rotate-180' : ''
                            }`}
                          />
                        </button>
                      ) : trimmedName.length > 0 ? (
                        <div className="flex items-center gap-2 pb-2 border-b border-input">
                          <Avatar className="size-8">
                            <AvatarImage src={previewSrc || undefined} className="object-cover" />
                            <AvatarFallback>{initials || '?'}</AvatarFallback>
                          </Avatar>
                          <span className="text-sm font-medium">{trimmedName}</span>
                        </div>
                      ) : (
                        <span className="text-sm text-tertiary">{t('speakersForm.guest')}</span>
                      )}
                      {isExpanded && (
                        <>
                          <div className="flex flex-col gap-4 sm:flex-row">
                            <div className="flex-1">
                              <TextFieldWithLimit
                                label={t('speakersForm.fullName')}
                                value={value?.fullName ?? ''}
                                onChange={(v) =>
                                  setValue(`speakers.${index}.fullName`, v, {
                                    shouldValidate: true,
                                  })
                                }
                                error={fieldErrors?.fullName?.message}
                              />
                            </div>
                            <div className="flex-1">
                              <TextFieldWithLimit
                                label={t('speakersForm.jobTitle')}
                                value={value?.jobTitle ?? ''}
                                onChange={(v) =>
                                  setValue(`speakers.${index}.jobTitle`, v, {
                                    shouldValidate: true,
                                  })
                                }
                                error={fieldErrors?.jobTitle?.message}
                              />
                            </div>
                          </div>
                          <div>
                            <TextFieldWithLimit
                              label={t('speakersForm.email')}
                              type="email"
                              value={value?.email ?? ''}
                              onChange={(v) =>
                                setValue(`speakers.${index}.email`, v, {
                                  shouldValidate: true,
                                })
                              }
                              error={fieldErrors?.email?.message}
                            />
                          </div>
                          <div className="flex flex-col gap-2">
                            <Label className="text-sm text-tertiary">
                              {t('speakersForm.picture')}
                            </Label>
                            <div className="flex items-center gap-4">
                              <input
                                type="file"
                                id={`speaker-picture-${field.id}`}
                                className="hidden"
                                accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
                                onChange={(e) => handlePictureSelect(field.id, e.target.files?.[0])}
                              />
                              {previewSrc ? (
                                <Avatar className="size-16">
                                  <AvatarImage src={previewSrc} className="object-cover" />
                                  <AvatarFallback>{initials || '?'}</AvatarFallback>
                                </Avatar>
                              ) : (
                                <Label
                                  htmlFor={`speaker-picture-${field.id}`}
                                  className="flex size-16 cursor-pointer items-center justify-center rounded-lg border-2 border-dashed border-card transition-colors hover:border-primary/50"
                                >
                                  <div className="flex size-8 items-center justify-center rounded-lg bg-[#EAF0FF]">
                                    <Plus className="size-5" />
                                  </div>
                                </Label>
                              )}
                              <div className="flex flex-col gap-1">
                                <div className="flex items-center gap-3">
                                  <Label
                                    htmlFor={`speaker-picture-${field.id}`}
                                    className="cursor-pointer text-sm font-medium text-primary hover:underline"
                                  >
                                    {previewSrc
                                      ? t('speakersForm.changePicture')
                                      : t('speakersForm.addPicture')}
                                  </Label>
                                  {previewSrc && (
                                    <button
                                      type="button"
                                      className="text-sm text-destructive hover:underline"
                                      onClick={() => handlePictureRemove(field.id)}
                                    >
                                      {t('speakersForm.removePicture')}
                                    </button>
                                  )}
                                </div>
                                <p className="text-xs text-secondary">
                                  {t('speakersForm.pictureHint')}
                                </p>
                              </div>
                            </div>
                            {pictureErrors[field.id] && (
                              <p className="mt-1 text-xs text-red-500">{pictureErrors[field.id]}</p>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                  {canRemoveRow && (
                    <button
                      type="button"
                      aria-label={t('speakersForm.removeSpeaker')}
                      onClick={() => handleRemoveSpeakerRow(index)}
                      disabled={deletingSpeakerId === value?.speakerId}
                      className="bg-white rounded-lg px-2 h-17 border border-input cursor-pointer disabled:opacity-40"
                    >
                      {deletingSpeakerId === value?.speakerId ? <Loader /> : <Trash2 />}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <button
        type="button"
        onClick={addSpeakerForm}
        disabled={isAtMax}
        className="mt-2 flex items-center gap-2 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <Plus className="size-6" />
        <Label className={isAtMax ? 'cursor-not-allowed' : 'cursor-pointer'}>
          {t('speakersForm.addSpeaker')}
        </Label>
      </button>

      <div className="w-full mt-4">
        <Button
          type="submit"
          variant="default"
          disabled={isSubmitting || !isValid}
          className="w-full"
        >
          {isSubmitting && <Loader />}
          {t('save')}
        </Button>
      </div>
    </form>
  );
}
